import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, TextField, Button, FormControl, FormHelperText, CardContent, Card, Grid, CardHeader } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';
import { useDispatch, useSelector } from 'react-redux';
import { usePostCheckPinOperationMutation, usePostEmailOperationMutation, usePutOperationMutation } from 'redux/api/operationApi';
import { setCurrentOperation } from 'redux/features/operationSlice';
import { useIntl } from 'react-intl';

const SignatureComponent: React.FC<{ nextStep: () => void, previousStep: () => void }> = ({ nextStep, previousStep }) => {
    const dispatch = useDispatch();
        const { formatMessage } = useIntl();
    
    const [updateOperation, { data: postedOperationData, isSuccess: isPostedOperationSuccess, error: apiError }] = usePutOperationMutation()
    const [postEmailOperation, { data: postedEmailOperationData, isSuccess: isPostedEmailOperationSuccess, error: apiEmailError }] = usePostEmailOperationMutation()
    const [postChekcPinOperation, { data: postedChekcPinOperationData, isSuccess: isPostedChekcPinOperationSuccess, error: apiChekcPinError }] = usePostCheckPinOperationMutation()

    const sigCanvas = useRef<SignatureCanvas>(null); // Referencia al lienzo de la firma
    const [clientEmail, setClientEmail] = useState('');
    const [isSigned, setIsSigned] = useState(false);
    const [error, setError] = useState(false);
    const [imgSrc, setImgSrc] = useState<string | null>(null);
    const [emailPin, setEmailPin] = useState('');
    const [countdown, setCountdown] = useState(0);

    const IMAGE_SERVER = process.env.REACT_APP_IMAGES_ENDPOINT;

    const operation = useSelector((state: any) => {
        return state.operationState?.currentOperation?.data || state.operationState?.currentOperation;
    });

    useEffect(() => {
        if (operation?.clientEmail) {
            setClientEmail(operation.clientEmail);
        }
        if (operation?.signature) {
            setImgSrc(`${IMAGE_SERVER}/${operation.signature}`);
        }
        if (operation?.signature && sigCanvas.current) {
            const canvas = sigCanvas.current.getCanvas();
            const context = canvas.getContext('2d');

            // Limpia el lienzo antes de cargar la firma
            sigCanvas.current.clear();

            // Carga la firma desde la URL y ajusta al tamaño del lienzo
            const image = new Image();
            image.src = operation.signature;
            image.onload = () => {
                context?.drawImage(image, 0, 0, canvas.width, canvas.height);
            };
        }
        // Cargar countdown si hay uno en localStorage
        const savedCountdown = localStorage.getItem('pinCountdown');
        if (savedCountdown) {
            const timeLeft = Number(savedCountdown) - Date.now();
            if (timeLeft > 0) setCountdown(Math.ceil(timeLeft / 1000));
        }
    }, []);

    useEffect(() => {
        if (countdown > 0) {
            const interval = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            localStorage.removeItem('pinCountdown'); // Limpiar countdown si llegó a 0
        }
    }, [countdown]);

    const base64ToBlob = (base64: string, contentType: string): Blob => {
        const byteString = atob(base64.split(',')[1]); // Decodifica la parte base64
        const mimeType = contentType || base64.split(',')[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
        const arrayBuffer = new Uint8Array(byteString.length);

        for (let i = 0; i < byteString.length; i++) {
            arrayBuffer[i] = byteString.charCodeAt(i);
        }

        return new Blob([arrayBuffer], { type: mimeType });
    };

    const handleSign = () => {
        if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
            setIsSigned(true);
            setImgSrc(null);
            handleSubmit();
        }
    };

    const handleClear = () => {
        if (sigCanvas.current) {
            sigCanvas.current.clear();
            setIsSigned(false);
        }
    };

    const handleSubmit = () => {
        if (clientEmail === ''  || !sigCanvas.current) {
            setError(true)
            return
        }
        const newSignatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
        
        if(sigCanvas.current?.isEmpty()&&!imgSrc){
            setError(true)
            return
        }
        
        // 🔴 Verificar si la firma ha cambiado antes de permitir avanzar
        if (operation?.emailPinVerified && IMAGE_SERVER && imgSrc?.startsWith(IMAGE_SERVER)) {
            console.log("La firma no ha cambiado, avanzando sin actualizar.");
            nextStep();
            return;
        }
        setError(false)
        const signatureBlob = base64ToBlob(newSignatureData, 'image/png');
        const operationData = {
            ...operation,
            signature: newSignatureData,
            clientEmail: clientEmail,
        };
        const formData = new FormData();
        formData.append('data', JSON.stringify(operationData));
        formData.append('signature', signatureBlob, `signature-${Date.now()}.png`);
        updateOperation({ id: operation.id, operationToUpdate: formData }).then((res) => {
            if ('data' in res) {
                dispatch(setCurrentOperation(res.data));
                nextStep();
            }
        })

    };
    const handleSendPin = () => {
        if (countdown > 0) return; // Evitar múltiples envíos
        const operationData = {
            ...operation,
            clientEmail: clientEmail
        };
        const formData = new FormData();
        formData.append('data', JSON.stringify(operationData));
        postEmailOperation({ id: operation.id, operationToUpdate: formData }).then((res) => {
            if ('data' in res) {
                dispatch(setCurrentOperation(res.data));
                // Iniciar countdown de 60 segundos
                setCountdown(60);
                localStorage.setItem('pinCountdown', String(Date.now() + 60000)); // Guardar timestamp de expiración
            }
        })

    }
    const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPin = e.target.value;

        // Limitar a 4 caracteres
        if (newPin.length <= 4) {
            setEmailPin(newPin);

            // Llamar a la función de verificación al alcanzar 4 dígitos
            if (newPin.length === 4) {
                verifyPin(newPin);
            }
        }
    };

    const verifyPin = (pin: string) => {
        const operationData = {
            ...operation,
            emailPin: pin
        };
        const formData = new FormData();
        formData.append('data', JSON.stringify(operationData));
        postChekcPinOperation({ id: operation.id, operationToUpdate: formData }).then((res) => {
            if ('data' in res) {
                dispatch(setCurrentOperation(res.data));
            }
        })
    };

    useEffect(() => {
        const canvasElement = sigCanvas.current?.getCanvas();
    
        const disableScroll = () => {
            document.body.style.overflow = 'hidden'; // Desactiva el scroll
        };
    
        const enableScroll = () => {
            document.body.style.overflow = 'auto'; // Reactiva el scroll
        };
    
        if (canvasElement) {
            // Agrega los listeners al canvas
            canvasElement.addEventListener('touchstart', disableScroll);
            canvasElement.addEventListener('touchend', enableScroll);
        }
    
        return () => {
            if (canvasElement) {
                // Elimina los listeners al desmontar el componente
                canvasElement.removeEventListener('touchstart', disableScroll);
                canvasElement.removeEventListener('touchend', enableScroll);
            }
        };
    }, [sigCanvas]);
    

    return (
        <Card sx={{ p: 2 }}>
            <CardHeader
                title={
                    <Typography variant="h1" align="center">
                        {formatMessage({ id: "sign.title" })}
                    </Typography>
                }
            />
            <CardContent>
                <FormControl
                    error={error}
                    sx={{ width: '100%' }}
                >

                    <Box>
                        <TextField
                            type="email"
                            placeholder={formatMessage({ id: "sign.email" })}
                            value={clientEmail}
                            onChange={(e) => setClientEmail(e.target.value)}
                            fullWidth
                            sx={{
                                marginBottom: '10px',
                                backgroundColor: 'white',
                                borderColor: '#000',
                            }}
                        />
                        {!(isPostedChekcPinOperationSuccess || operation.emailPinVerified) && (
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        marginBottom: '20px',
                                        color: '#555',
                                    }}
                                >
                                    {formatMessage({ id: "sign.instructons" })}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex', // Coloca los elementos en fila
                                        alignItems: 'center', // Alinea verticalmente los elementos
                                        justifyContent: 'space-between', // Espaciado entre los elementos
                                        gap: '10px', // Espaciado entre el botón y el input
                                        marginBottom: '20px', // Espaciado inferior
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        onClick={handleSendPin}
                                        disabled={countdown > 0}
                                        sx={{
                                            backgroundColor: countdown > 0 ? '#B3B3B3' : 'black',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            '&:hover': {
                                                backgroundColor: countdown > 0 ? '#B3B3B3' : '#333',
                                            },
                                            width: '200px',
                                            height: '64px',
                                        }}
                                    >
                                        {countdown > 0 ? formatMessage({ id: "sign.resend" }) + countdown : formatMessage({ id: "sign.generate.pin" })}
                                    </Button>

                                    <TextField
                                        type="emailPin"
                                        placeholder="PIN"
                                        value={emailPin}
                                        onChange={handlePinChange}
                                        fullWidth
                                        sx={{
                                            backgroundColor: 'white',
                                            borderColor: '#000',
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            marginBottom: '20px',
                                            color: '#555',
                                        }}
                                    >
                                        {formatMessage({ id: "sign.put.pin" })}
                                    </Typography>
                                    {apiChekcPinError && <p style={{ color: 'red' }}>{formatMessage({ id: "sign.unvalid.pin" })}</p>}

                                </Box>
                            </Box>                            
                        )}
                        

                    </Box>
                    {(isPostedChekcPinOperationSuccess || operation.emailPinVerified) && (
                        <Box>

                            {operation?.emailPinVerified && <p style={{ color: 'green' }}>{formatMessage({ id: "sign.email.verified" })}</p>}
                            {/* Área de Firma */}
                            <Box
                                sx={{
                                    width: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {/* Texto "Dibujar" por encima del canvas y centrado */}
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 'bold',
                                        marginBottom: '10px',
                                        textAlign: 'center', // Centrado horizontal
                                    }}
                                >
                                    {formatMessage({ id: "sign.draw" })}
                                </Typography>
                                {/* Contenedor del canvas */}
                                <Box
                                    sx={{
                                        border: '2px solid black',
                                        borderRadius: '4px',
                                        height: '200px',
                                        width: '330px',
                                        marginBottom: '10px',
                                        backgroundColor: 'white',
                                    }}
                                >
                                    <SignatureCanvas
                                        ref={sigCanvas}
                                        penColor="black"
                                        canvasProps={{
                                            width: 330, // Valor base para pantallas grandes
                                            height: 198,
                                            className: 'sigCanvas',
                                            style: {
                                                pointerEvents: operation?.emailPinVerified ? 'auto' : 'none', // Bloquea si no está verificado
                                                width: '100%', // Ancho base para adaptarse
                                                maxWidth: '330px', // Límite máximo para pantallas grandes
                                                height: 'auto', // Mantener la relación de aspecto
                                            },
                                        }}
                                    />
                                    {!operation?.emailPinVerified && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                zIndex: 1,
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: '#555' }}>
                                            {formatMessage({ id: "sign.verify.mail" })}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            {error && (
                                <FormHelperText
                                    sx={{
                                        marginBottom: '10px',
                                    }}>
                                    {formatMessage({ id: "sign.confirm" })}
                                </FormHelperText>
                            )}
                            {/* Botones para limpiar y guardar */}
                            <Box sx={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSign}
                                    sx={{
                                        backgroundColor: !operation?.emailPinVerified ? '#B3B3B3' : 'black',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            backgroundColor: !operation?.emailPinVerified ? '#B3B3B3' : '#333',
                                        },
                                    }}
                                >
                                    {formatMessage({ id: "buttons.sign" })}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleClear}
                                    disabled={!operation?.emailPinVerified}
                                    sx={{
                                        border: '2px solidrgb(88, 72, 72)',
                                        color: '#B3B3B3',
                                        fontWeight: 'bold',
                                        backgroundColor: 'white',
                                        '&:hover': {
                                            backgroundColor: '#f2f2f2',
                                        },
                                    }}
                                >
                                    {formatMessage({ id: "buttons.cancel" })}
                                </Button>
                            </Box>
                            {imgSrc && (
                                <div style={{ textAlign: 'center' }}>
                                    <h3>{formatMessage({ id: "sign.previous" })}</h3>
                                    <img
                                        src={imgSrc}
                                        alt="Firma pre-existente:"
                                        style={{
                                            width: '100px',
                                            height: 'auto',
                                            borderRadius: '10px',
                                            marginTop: '10px',
                                            backgroundColor: 'white',
                                        }}
                                    />
                                </div>
                            )}
                        </Box>
                    )}

                    <Grid container spacing={2} justifyContent="space-between">
                        <Grid item xs={12} lg={6}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                onClick={previousStep}
                                sx={{
                                    mt: 2,
                                    backgroundColor: "#FFF",
                                    display: {
                                        xs: "none", // Ocultar en pantallas pequeñas (móviles)
                                        sm: "none", // Ocultar en tablets
                                        md: "block", // Mostrar en pantallas medianas y grandes
                                    },
                                }}
                            >
                                {formatMessage({ id: "buttons.back" })}
                            </Button>
                        </Grid>
                    </Grid>
                </FormControl>
            </CardContent>
        </Card>
    );
};

export default SignatureComponent;
